// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The playfield glass (M3-09).
//
// A sheet of glass over a pinball playfield is invisible from directly above and unmistakable
// from anywhere else, and the difference is entirely **fresnel**: a dielectric reflects almost
// nothing when you look through it square-on and almost everything at a grazing angle. That is
// why the far end of a real machine's playfield is half hidden behind a sheen of reflected
// backglass while the ball in front of you is perfectly clear.
//
// So this is worth drawing only because there is an environment map to reflect (M3-08). Glass
// with nothing to reflect is a grey veil that dims the artwork and adds nothing — which is
// what it would have been if this had been built before the environment was.
//
// Three things keep it from wrecking everything behind it:
//
//   * **`depthWrite: false`.** Glass covers the entire playfield. Writing depth would put it in
//     front of every transparent thing under it in the sort, and the ramps would vanish.
//   * **A high `renderOrder`.** It has to be drawn after everything it is in front of.
//   * **`side: FrontSide`.** The underside is never seen — the camera is never inside the
//     cabinet — and drawing both faces doubles the cost of the largest transparent surface in
//     the scene for a view nobody has.

import {
  FrontSide,
  Mesh,
  MeshPhysicalMaterial,
  PlaneGeometry,
  type BufferGeometry,
  type Material,
} from 'three'
import { FINISHES } from './partsKit'

export interface BuiltGlass {
  readonly mesh: Mesh
  readonly geometries: readonly BufferGeometry[]
  readonly materials: readonly Material[]
}

/**
 * The glass, in playfield-local coordinates.
 *
 * `height` is the machine's own `dimensions.glassHeight` — the underside of the glass above
 * the playfield surface, which is the number the simulation already uses to decide whether a
 * ball can get under a ramp. Drawing it anywhere else would put the picture and the physics in
 * two different places on the one measurement they visibly share.
 */
export function buildGlass(width: number, length: number, height: number): BuiltGlass {
  const geometry = new PlaneGeometry(width, length)
  const material = new MeshPhysicalMaterial({
    // Colour, roughness, opacity and depth from the machine's one palette, so the browser and the
    // headset put the same sheet of glass over the same playfield.
    ...FINISHES.playfieldGlass,
    transparent: true,
    // The whole effect, and browser-only. `reflectivity` at a dielectric's real value gives the
    // fresnel curve that makes the glass disappear head-on and appear at a glancing angle.
    reflectivity: 0.5,
    ior: 1.52,
    side: FrontSide,
  })

  const mesh = new Mesh(geometry, material)
  mesh.name = 'glass'
  mesh.position.z = height
  // After everything it covers. Three sorts transparent objects back to front by distance, and
  // a flat sheet spanning the whole playfield has no single distance that is right.
  mesh.renderOrder = 10

  return { mesh, geometries: [geometry], materials: [material] }
}
